import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import api from "../../lib/api";
import { toast } from "sonner";
import {
  X, FileText, ClockCounterClockwise, Notepad, Handshake, Pulse,
  Trophy, XCircle, ChatCircleText, CheckCircle, Plus, Eye, Copy, ArrowRight, EnvelopeSimple, Phone,
} from "@phosphor-icons/react";
import FollowupStatusSelect, { statusBadgeCls } from "./FollowupStatusSelect";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ----- Pipeline stages (forward-only progression) -----
const PIPELINE = [
  { key: "new", label: "New" },
  { key: "contacted", label: "Contacted" },
  { key: "quotation_sent", label: "Quotation Sent" },
  { key: "negotiation", label: "Negotiation" },
  { key: "sales_order_created", label: "Sales Order" },
  { key: "invoice_raised", label: "Invoice" },
  { key: "payment_collected", label: "Payment" },
  { key: "won", label: "Won/Lost" },
];

const stageIndex = (status) => {
  if (!status) return 0;
  const s = String(status).toLowerCase();
  if (s === "lost") return PIPELINE.length - 1; // shown distinctly
  const i = PIPELINE.findIndex((p) => p.key === s);
  return i >= 0 ? i : 0;
};

// ----- Pipeline strip -----
const PipelineStrip = ({ status }) => {
  const idx = stageIndex(status);
  const isLost = String(status || "").toLowerCase() === "lost";
  return (
    <div className="flex items-stretch gap-1 px-5 py-3 border-b border-slate-200 bg-slate-50" data-testid="lead-drawer-pipeline">
      {PIPELINE.map((p, i) => {
        const reached = i <= idx;
        const isFinal = i === PIPELINE.length - 1;
        const label = isFinal ? (isLost ? "Lost" : reached ? "Won" : "Won/Lost") : p.label;
        const color = isFinal && reached
          ? (isLost ? "bg-red-600 text-white border-red-600" : "bg-emerald-600 text-white border-emerald-600")
          : reached
            ? "bg-[#002FA7] text-white border-[#002FA7]"
            : "bg-white text-slate-500 border-slate-300";
        return (
          <div key={p.key} className="flex-1 min-w-0" data-testid={`stage-${p.key}`}>
            <div className={`text-[10px] uppercase tracking-wider px-2 py-1.5 border text-center truncate ${color}`}>
              {label}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ----- Tab definitions -----
const TABS = [
  { key: "overview", label: "Overview", icon: ChatCircleText },
  { key: "followups", label: "Follow-ups", icon: ClockCounterClockwise },
  { key: "quotations", label: "Quotations", icon: FileText },
  { key: "deals", label: "Deals", icon: Handshake },
  { key: "activities", label: "Activities", icon: Pulse },
  { key: "notes", label: "Notes", icon: Notepad },
];

// ===== Tab content components (lazy-mounted: only rendered when active) =====

const OverviewTab = ({ lead }) => (
  <div className="p-5 space-y-4 text-sm" data-testid="lead-tab-overview">
    <Section title="Contact">
      <Row label="Name" value={lead.name} />
      <Row label="Phone" value={lead.phone} icon={<Phone size={12} />} />
      {lead.whatsapp && lead.whatsapp !== lead.phone && <Row label="WhatsApp" value={lead.whatsapp} />}
      <Row label="Email" value={lead.email} icon={<EnvelopeSimple size={12} />} />
      <Row label="Company" value={lead.company_name} />
      <Row label="City" value={lead.city || lead.state} />
    </Section>
    <Section title="Lead Details">
      <Row label="Source" value={lead.source} />
      <Row label="Priority" value={lead.priority} />
      <Row label="Lead Score" value={String(lead.lead_score ?? "—")} />
      <Row label="Budget" value={lead.budget ? `₹${Number(lead.budget).toLocaleString("en-IN")}` : "—"} />
      <Row label="Assigned" value={lead.assigned_to} />
      {lead.assigned_bdm && <Row label="BDM" value={lead.assigned_bdm} />}
    </Section>
    {/* Audit Trail — Phase Audit-1 */}
    <Section title="Audit Trail">
      <Row label="Created By" value={lead.created_by_name ? `${lead.created_by_name} · ${(lead.created_by_role||'').toUpperCase()}` : 'Unknown'} />
      <Row label="Created Source" value={(lead.created_source || '—').replace(/_/g, ' ')} />
      <Row label="Created At" value={lead.created_at ? new Date(lead.created_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '—'} />
      {lead.assigned_to && (
        <>
          <Row label="Assigned To" value={`${lead.assigned_to} · ${(lead.assigned_to_role||'—').toUpperCase()}`} />
          <Row label="Assigned By" value={lead.assigned_by_name ? `${lead.assigned_by_name} · ${(lead.assigned_by_role||'—').toUpperCase().replace(/_/g, ' ')}` : 'Unknown'} />
          <Row label="Assigned At" value={lead.assigned_at ? new Date(lead.assigned_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '—'} />
        </>
      )}
      {lead.followup_owner_name && <Row label="Follow-up Owner" value={lead.followup_owner_name} />}
    </Section>
    {lead.product_requirement && (
      <Section title="Requirement">
        <p className="text-slate-700 whitespace-pre-wrap">{lead.product_requirement}</p>
      </Section>
    )}
    {lead.notes && (
      <Section title="Notes (preview)">
        <p className="text-slate-600 text-xs whitespace-pre-wrap line-clamp-4">{lead.notes}</p>
      </Section>
    )}
  </div>
);

const Section = ({ title, children }) => (
  <div>
    <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5 font-semibold">{title}</div>
    <div className="border border-slate-200 bg-white p-3 space-y-1.5">{children}</div>
  </div>
);

const Row = ({ label, value, icon }) => (
  <div className="grid grid-cols-3 gap-2 items-baseline">
    <span className="text-xs text-slate-500 col-span-1">{label}</span>
    <span className="text-sm text-slate-900 col-span-2 inline-flex items-center gap-1.5">{icon}{value || "—"}</span>
  </div>
);

const FollowUpsTab = ({ lead, user, onLeadChange }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ scheduled_for: "", note: "", status: "" });
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const r = await api.get(`${API}/leads/${lead.lead_id}/follow-ups`);
      setItems(r.data || []);
    } finally { setLoading(false); }
  }, [lead.lead_id]);

  useEffect(() => { refresh(); }, [refresh]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.scheduled_for) { toast.error("Pick a date/time"); return; }
    setBusy(true);
    try {
      // Convert local datetime to ISO string
      const scheduledDate = new Date(form.scheduled_for);
      const isoDate = scheduledDate.toISOString();
      await api.post(`${API}/leads/${lead.lead_id}/follow-ups`, {
        lead_id: lead.lead_id,
        type: "call",
        remarks: form.note || "",
        next_follow_up_at: isoDate,
        outcome: "scheduled",
        status: form.status || "call_scheduled",
        by_user_id: user?.user_id || "",
        by_user_name: user?.name || "",
      });
      toast.success("Follow-up added");
      setForm({ scheduled_for: "", note: "", status: "" });
      refresh();
      onLeadChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="p-5 space-y-4" data-testid="lead-tab-followups">
      <form onSubmit={submit} className="border border-slate-200 bg-white p-3 space-y-2" data-testid="lead-add-followup-form">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Add Follow-up</div>
        <input type="datetime-local" value={form.scheduled_for} onChange={(e) => setForm({ ...form, scheduled_for: e.target.value })}
          className="w-full border border-slate-300 px-2 py-1.5 text-sm" data-testid="followup-datetime" />
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Status</div>
          <FollowupStatusSelect
            value={form.status}
            onChange={(v) => setForm({ ...form, status: v })}
            user={user}
            testId="followup-status-form"
          />
        </div>
        <textarea rows={2} placeholder="Note (optional)" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })}
          className="w-full border border-slate-300 px-2 py-1.5 text-sm" data-testid="followup-note" />
        <button type="submit" disabled={busy} className="bg-[#002FA7] text-white text-xs uppercase tracking-wider px-3 py-1.5 inline-flex items-center gap-1.5 disabled:opacity-60" data-testid="followup-submit">
          <Plus size={12} weight="bold" /> {busy ? "Adding…" : "Add"}
        </button>
      </form>

      <div>
        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">History ({items.length})</div>
        {loading ? <div className="text-sm text-slate-500">Loading…</div>
          : items.length === 0 ? <div className="text-sm text-slate-500">No follow-ups yet.</div>
            : (
              <ul className="space-y-2">
                {items.map((f, i) => (
                  <li key={f.follow_up_id || i} className="border border-slate-200 bg-white p-3 text-sm" data-testid={`followup-row-${i}`}>
                    <div className="flex justify-between text-xs text-slate-500 mb-1">
                      <span>{f.next_follow_up_at ? new Date(f.next_follow_up_at).toLocaleString() : "—"}</span>
                      <span>{f.by_user_name || "—"}</span>
                    </div>
                    <div className="text-slate-800 whitespace-pre-wrap">{f.remarks || "—"}</div>
                    <div className="flex flex-wrap gap-1.5 mt-1.5 items-center">
                      {f.status && (
                        <span className={`${statusBadgeCls()} text-[10px] uppercase tracking-wider px-2 py-0.5 font-bold`} data-testid={`followup-status-badge-${i}`}>
                          {String(f.status).replace(/_/g, " ")}
                        </span>
                      )}
                      {f.outcome && <span className="text-[10px] uppercase tracking-wider text-emerald-700">→ {f.outcome}</span>}
                    </div>
                  </li>
                ))}
              </ul>
            )
        }
      </div>
    </div>
  );
};

const QuotationsTab = ({ lead, user, onLeadChange, openQuickQuote }) => {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/billing?requester_role=${user?.role || ""}&doc_type=estimation&limit=200`);
        if (cancelled) return;
        const all = Array.isArray(r.data) ? r.data : [];
        const mine = all.filter((q) => q.source_lead_id === lead.lead_id || (lead.linked_quotation_ids || []).includes(q.doc_id));
        setQuotes(mine);
      } catch { /* silently empty */ } finally { setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [lead.lead_id, lead.linked_quotation_ids, user?.role]);

  return (
    <div className="p-5 space-y-3" data-testid="lead-tab-quotations">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Quotations ({quotes.length})</div>
        <button onClick={openQuickQuote} className="text-[10px] uppercase tracking-wider px-2 py-1 border border-emerald-300 text-emerald-700 hover:bg-emerald-50 font-bold inline-flex items-center gap-1" data-testid="qtab-create-quote">
          <FileText size={12} weight="bold" /> Create Quotation
        </button>
      </div>
      {loading ? <div className="text-sm text-slate-500">Loading…</div>
        : quotes.length === 0 ? <div className="text-sm text-slate-500 p-3 border border-dashed border-slate-300">No quotations yet — click <span className="font-semibold">Create Quotation</span> to generate one.</div>
          : (
            <ul className="space-y-2">
              {quotes.map((q) => (
                <li key={q.doc_id} className="border border-slate-200 bg-white p-3 text-sm space-y-1.5" data-testid={`qtab-row-${q.doc_id}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-semibold text-slate-900">{q.number || q.doc_id.slice(0, 8)}</div>
                      <div className="text-xs text-slate-500">{q.subject || q.customer_name}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-semibold">₹{Number(q.total_amount || 0).toLocaleString("en-IN")}</div>
                      <div className="inline-block text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-slate-100 text-slate-700 mt-0.5">{q.status || "draft"}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 pt-1.5">
                    <button onClick={() => navigate("/billing")} className="text-[10px] uppercase tracking-wider px-2 py-1 border border-slate-300 hover:bg-slate-50 font-medium inline-flex items-center gap-1" title="Open in Billing" data-testid={`qtab-view-${q.doc_id}`}>
                      <Eye size={10} weight="bold" /> View
                    </button>
                    <span className="text-xs text-slate-400 ml-auto">{q.expiry_date || q.valid_until || ""}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
    </div>
  );
};

const DealsTab = ({ lead }) => {
  const navigate = useNavigate();
  if (!lead.linked_deal_id) {
    return (
      <div className="p-5 text-sm text-slate-600" data-testid="lead-tab-deals">
        <div className="border border-dashed border-slate-300 p-4 text-center">
          No deal yet. A deal is auto-created the first time you click <span className="font-semibold">Create Quotation</span>.
        </div>
      </div>
    );
  }
  return (
    <div className="p-5 space-y-3" data-testid="lead-tab-deals">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Linked Deal</div>
      <div className="border border-slate-200 bg-white p-3 text-sm">
        <div className="font-semibold text-slate-900">{lead.linked_deal_title || "—"}</div>
        <div className="text-xs text-slate-500 mt-1">Deal ID: {lead.linked_deal_id}</div>
        <button onClick={() => navigate("/crm/deals")} className="mt-2 text-[10px] uppercase tracking-wider px-2 py-1 border border-blue-300 text-blue-700 hover:bg-blue-50 font-bold inline-flex items-center gap-1" data-testid="dtab-open-deals">
          Open Deals Pipeline <ArrowRight size={10} weight="bold" />
        </button>
      </div>
    </div>
  );
};

const ActivitiesTab = ({ lead, quotationsCache }) => {
  // Synthesized timeline from data we already have (no backend call needed).
  const timeline = useMemo(() => {
    const items = [];
    if (lead.created_at) {
      items.push({ when: lead.created_at, type: "created", text: `Lead created${lead.source ? ` via ${lead.source}` : ""}` });
    }
    if (lead.converted_at) {
      items.push({ when: lead.converted_at, type: "converted", text: `Converted to deal${lead.linked_deal_title ? `: ${lead.linked_deal_title}` : ""}` });
    }
    (quotationsCache || []).forEach((q) => {
      if (q.created_at) {
        items.push({ when: q.created_at, type: "quote", text: `Quotation ${q.number || q.doc_id?.slice(0, 8)} created · ₹${Number(q.total_amount || 0).toLocaleString("en-IN")}` });
      }
    });
    return items.sort((a, b) => String(b.when || "").localeCompare(String(a.when || "")));
  }, [lead, quotationsCache]);

  return (
    <div className="p-5" data-testid="lead-tab-activities">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Timeline ({timeline.length})</div>
      {timeline.length === 0 ? <div className="text-sm text-slate-500">No activity yet.</div>
        : (
          <ol className="relative border-l border-slate-200 ml-2 space-y-3">
            {timeline.map((t, i) => (
              <li key={i} className="ml-4" data-testid={`atab-row-${i}`}>
                <span className={`absolute -left-1.5 mt-1 h-3 w-3 rounded-full border-2 border-white ${t.type === "quote" ? "bg-emerald-500" : t.type === "converted" ? "bg-blue-500" : "bg-slate-400"}`} />
                <div className="text-xs text-slate-500">{t.when ? new Date(t.when).toLocaleString() : "—"}</div>
                <div className="text-sm text-slate-800">{t.text}</div>
              </li>
            ))}
          </ol>
        )}
    </div>
  );
};

const NotesTab = ({ lead, user, onLeadChange }) => {
  const [val, setVal] = useState(lead.notes || "");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setVal(lead.notes || ""); }, [lead.notes]);

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`${API}/leads/${lead.lead_id}`, { ...lead, notes: val });
      toast.success("Notes saved");
      onLeadChange?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="p-5 space-y-3" data-testid="lead-tab-notes">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Internal Notes</div>
      <textarea rows={10} value={val} onChange={(e) => setVal(e.target.value)} placeholder="Add internal team notes…"
        className="w-full border border-slate-300 px-3 py-2 text-sm" data-testid="notes-textarea" />
      <button onClick={save} disabled={busy} className="bg-[#002FA7] text-white text-xs uppercase tracking-wider px-3 py-1.5 disabled:opacity-60" data-testid="notes-save">
        {busy ? "Saving…" : "Save Notes"}
      </button>
    </div>
  );
};

// ===== Main drawer =====
export default function LeadDetailDrawer({ lead, user, onClose, onLeadChange, onQuickQuote }) {
  const [tab, setTab] = useState("overview");
  const [busyMark, setBusyMark] = useState(null);
  const [quotesCache, setQuotesCache] = useState([]);

  // Pre-fetch quotations once for activities-tab synthesis (cheap; cached).
  useEffect(() => {
    if (!lead) return;
    let cancelled = false;
    api.get(`/billing?requester_role=${user?.role || ""}&doc_type=estimation&limit=200`)
      .then((r) => {
        if (cancelled) return;
        const all = Array.isArray(r.data) ? r.data : [];
        setQuotesCache(all.filter((q) => q.source_lead_id === lead.lead_id || (lead.linked_quotation_ids || []).includes(q.doc_id)));
      })
      .catch(() => setQuotesCache([]));
    return () => { cancelled = true; };
  }, [lead, user?.role]);

  // Keyboard: Esc closes
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (!lead) return null;

  const markStatus = async (status, label) => {
    setBusyMark(status);
    try {
      await api.put(`${API}/leads/${lead.lead_id}/status?status=${status}`);
      toast.success(`Marked ${label}`);
      onLeadChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusyMark(null); }
  };

  return (
    <div className="fixed inset-0 z-[150] flex justify-end" data-testid="lead-detail-drawer-root">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} data-testid="lead-drawer-backdrop" />

      <aside className="relative bg-white w-full max-w-2xl h-full shadow-2xl flex flex-col animate-in slide-in-from-right duration-200" data-testid="lead-detail-drawer">
        {/* Header */}
        <div className="px-5 py-4 border-b border-slate-200 flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-xl font-semibold text-slate-900 truncate" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="lead-drawer-name">
                {lead.name || "—"}
              </h2>
              <StatusBadge status={lead.status} />
            </div>
            <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-3 flex-wrap">
              {lead.phone && <span className="inline-flex items-center gap-1"><Phone size={10} /> {lead.phone}</span>}
              {lead.assigned_to && <span>· {lead.assigned_to}</span>}
              {lead.source && <span>· via {lead.source}</span>}
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-none" data-testid="lead-drawer-close" title="Close (Esc)">
            <X size={18} />
          </button>
        </div>

        {/* Action buttons */}
        <div className="px-5 py-3 border-b border-slate-200 flex flex-wrap gap-2 bg-white">
          <button onClick={() => onQuickQuote?.(lead)} className="text-[10px] uppercase tracking-wider px-2.5 py-1.5 border border-emerald-300 text-emerald-700 hover:bg-emerald-50 font-bold inline-flex items-center gap-1" data-testid="lead-drawer-create-quote">
            <FileText size={12} weight="bold" /> Create Quotation
          </button>
          <button onClick={() => setTab("followups")} className="text-[10px] uppercase tracking-wider px-2.5 py-1.5 border border-blue-300 text-blue-700 hover:bg-blue-50 font-bold inline-flex items-center gap-1" data-testid="lead-drawer-add-followup">
            <ClockCounterClockwise size={12} weight="bold" /> Add Follow-up
          </button>
          <div className="ml-auto flex gap-2">
            <button
              onClick={() => markStatus("won", "Won")}
              disabled={busyMark === "won"}
              className="text-[10px] uppercase tracking-wider px-2.5 py-1.5 border border-emerald-600 text-emerald-700 hover:bg-emerald-600 hover:text-white font-bold inline-flex items-center gap-1 disabled:opacity-60"
              data-testid="lead-drawer-mark-won"
            >
              <Trophy size={12} weight="fill" /> Won
            </button>
            <button
              onClick={() => markStatus("lost", "Lost")}
              disabled={busyMark === "lost"}
              className="text-[10px] uppercase tracking-wider px-2.5 py-1.5 border border-red-500 text-red-600 hover:bg-red-600 hover:text-white font-bold inline-flex items-center gap-1 disabled:opacity-60"
              data-testid="lead-drawer-mark-lost"
            >
              <XCircle size={12} weight="bold" /> Lost
            </button>
          </div>
        </div>

        {/* Pipeline strip */}
        <PipelineStrip status={lead.status} />

        {/* Tabs */}
        <div className="flex border-b border-slate-200 bg-white sticky top-0 z-10">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                data-testid={`lead-drawer-tab-${t.key}`}
                className={`flex-1 px-3 py-2.5 text-xs uppercase tracking-wider font-semibold inline-flex items-center justify-center gap-1.5 transition-colors border-b-2 ${
                  active ? "border-[#002FA7] text-[#002FA7]" : "border-transparent text-slate-500 hover:text-slate-900 hover:bg-slate-50"
                }`}
              >
                <Icon size={13} weight={active ? "fill" : "bold"} /> {t.label}
              </button>
            );
          })}
        </div>

        {/* Lazy-loaded tab body */}
        <div className="flex-1 overflow-y-auto">
          {tab === "overview" && <OverviewTab lead={lead} />}
          {tab === "followups" && <FollowUpsTab lead={lead} user={user} onLeadChange={onLeadChange} />}
          {tab === "quotations" && <QuotationsTab lead={lead} user={user} onLeadChange={onLeadChange} openQuickQuote={() => onQuickQuote?.(lead)} />}
          {tab === "deals" && <DealsTab lead={lead} />}
          {tab === "activities" && <ActivitiesTab lead={lead} quotationsCache={quotesCache} />}
          {tab === "notes" && <NotesTab lead={lead} user={user} onLeadChange={onLeadChange} />}
        </div>
      </aside>
    </div>
  );
}

// Small status badge (mirrors the table's badges visually)
const StatusBadge = ({ status }) => {
  const s = String(status || "new").toLowerCase();
  const map = {
    new: "bg-blue-100 text-blue-700",
    contacted: "bg-amber-100 text-amber-700",
    qualified: "bg-purple-100 text-purple-700",
    quotation_sent: "bg-emerald-100 text-emerald-700",
    negotiation: "bg-fuchsia-100 text-fuchsia-700",
    sales_order_created: "bg-indigo-100 text-indigo-700",
    invoice_raised: "bg-cyan-100 text-cyan-700",
    payment_collected: "bg-emerald-100 text-emerald-700",
    converted: "bg-emerald-100 text-emerald-700",
    won: "bg-emerald-600 text-white",
    lost: "bg-red-100 text-red-700",
  };
  return <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 ${map[s] || "bg-slate-100 text-slate-700"}`} data-testid="lead-drawer-status-badge">{s.replace(/_/g, " ")}</span>;
};
